import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  AxiosStatic,
  CancelTokenSource as AxiosCancelTokenSource,
} from 'axios';
import { affectedSessionInterceptor, internalOnErrorInterceptor } from './interceptors';

export type FetchApiType = {
  <T>(config: AxiosRequestConfig): Promise<FetchApiResponse<T>>;
  get: <T>(endpoint: string, config?: AxiosRequestConfig) => Promise<FetchApiResponse<T>>;
  post: <T>(endpoint: string, data?: unknown, config?: AxiosRequestConfig) => Promise<FetchApiResponse<T>>;
  put: <T>(endpoint: string, data?: unknown, config?: AxiosRequestConfig) => Promise<FetchApiResponse<T>>;
};

export const API_ORIGIN = window.apiOriginUrl;

export const { CancelToken } = axios;
export type CancelTokenSource = AxiosCancelTokenSource;
export const isFetchApiCancel = axios.isCancel;

export type FetchApiResponse<T> = AxiosResponse<T>;
export type FetchApiError<T> = AxiosError<T>;

const createFetchApiInstance = () => {
  const fetchApiInstance = axios.create();

  fetchApiInstance.interceptors.request.use((config) => {
    affectedSessionInterceptor();

    return config;
  });

  fetchApiInstance.interceptors.response.use((response) => response, internalOnErrorInterceptor);

  return fetchApiInstance;
};

const createFetchExternalApiInstance = () => {
  return axios.create();
};

const fetchApiInstance = createFetchApiInstance();
const fetchExternalApiInstance = createFetchExternalApiInstance();

const getFetchApiBaseParams = (config: AxiosRequestConfig): AxiosRequestConfig => {
  const { url: inputURL } = config;

  if (!inputURL || !API_ORIGIN) {
    return config;
  }
  const url = new URL(inputURL, API_ORIGIN).toString();
  const withCredentials = url.startsWith(API_ORIGIN);

  return {
    ...config,
    url,
    withCredentials,
  };
};

const getFetchApiGetParams = (endpoint: string, config?: AxiosRequestConfig): AxiosRequestConfig => ({
  method: 'get',
  url: endpoint,
  ...config,
});

const getFetchApiPostParams = (endpoint: string, data?: unknown, config?: AxiosRequestConfig): AxiosRequestConfig => ({
  method: 'post',
  url: endpoint,
  data,
  ...config,
});

const getFetchApiPutParams = (endpoint: string, data?: unknown, config?: AxiosRequestConfig): AxiosRequestConfig => ({
  method: 'put',
  url: endpoint,
  data,
  ...config,
});

const buildFetchApi = (axiosInstance: AxiosInstance): AxiosStatic => {
  const fetchApiBase = async <T>(config: AxiosRequestConfig): Promise<FetchApiResponse<T>> => {
    return await axiosInstance(getFetchApiBaseParams(config));
  };

  const fetchApiGet = async <T>(endpoint: string, config?: AxiosRequestConfig): Promise<FetchApiResponse<T>> => {
    return await fetchApiBase(getFetchApiGetParams(endpoint, config));
  };

  const fetchApiPost = async <T>(
    endpoint: string,
    data?: never,
    config?: AxiosRequestConfig,
  ): Promise<FetchApiResponse<T>> => {
    return await fetchApiBase(getFetchApiPostParams(endpoint, data, config));
  };

  const fetchApiPut = async <T>(
    endpoint: string,
    data?: never,
    config?: AxiosRequestConfig,
  ): Promise<FetchApiResponse<T>> => {
    return await fetchApiBase(getFetchApiPutParams(endpoint, data, config));
  };

  return Object.assign(fetchApiBase, {
    get: fetchApiGet,
    post: fetchApiPost,
    put: fetchApiPut,
  }) as AxiosStatic;
};

export const fetchApi: FetchApiType = buildFetchApi(fetchApiInstance);

export const fetchExternalApi: FetchApiType = buildFetchApi(fetchExternalApiInstance);
